"use client"

import React, { useEffect, useState } from "react"
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet"
import "leaflet/dist/leaflet.css"
import L from "leaflet"
import { Spinner, Button, Alert, Form, InputGroup } from "react-bootstrap"

// Fix for default marker icon in Leaflet
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
})

const MapComponent = ({ onLocationSelect, loading }) => {
  const [position, setPosition] = useState(null)
  const [locationError, setLocationError] = useState(null)
  const [gettingLocation, setGettingLocation] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [searchResults, setSearchResults] = useState([])
  const [showResults, setShowResults] = useState(false)

  // Get user's current location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords
          setPosition([latitude, longitude])
        },
        (err) => {
          console.error("Error fetching current location:", err)
        }
      )
    }
  }, [])

  // Handle map click and marker drag
  const MapEvents = () => {
    useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng
        setPosition([lat, lng])
        onLocationSelect(lat, lng)
      },
    })
    return null
  }

  const handleMarkerDragEnd = (e) => {
    const { lat, lng } = e.target.getLatLng()
    setPosition([lat, lng])
    onLocationSelect(lat, lng)
  }

  const handleSearch = async (query) => {
    if (!query.trim() || query.length < 3) {
      setSearchResults([])
      setShowResults(false)
      return
    }

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`,
      )
      const results = await response.json()

      setSearchResults(results)
      setShowResults(results.length > 0)
    } catch (error) {
      console.error("Search error:", error)
      setLocationError("Search failed. Please try again.")
    }
  }

  const selectSearchResult = (result) => {
    const lat = Number.parseFloat(result.lat)
    const lng = Number.parseFloat(result.lon)

    setPosition([lat, lng])
    onLocationSelect(lat, lng)

    // Clear search
    setSearchValue("")
    setShowResults(false)
    setSearchResults([])
  }

  const getCurrentLocation = () => {
    setGettingLocation(true)
    setLocationError(null)

    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by this browser")
      setGettingLocation(false)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        setPosition([latitude, longitude])
        onLocationSelect(latitude, longitude)
        setGettingLocation(false)
      },
      (error) => {
        let errorMessage = "Unable to retrieve your location"

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location access denied by user"
            break
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information is unavailable"
            break
          case error.TIMEOUT:
            errorMessage = "Location request timed out"
            break
          default:
            errorMessage = "An unknown error occurred"
            break
        }

        setLocationError(errorMessage)
        setGettingLocation(false)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      },
    )
  }

  return (
    <div className="position-relative">
      <div className="mb-3 position-relative">
        <InputGroup>
          <InputGroup.Text>
            <i className="fas fa-search"></i>
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search for a location (e.g., New York, NY or 123 Main St)"
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value)
              handleSearch(e.target.value)
            }}
            disabled={loading}
            onFocus={() => searchResults.length > 0 && setShowResults(true)}
            onBlur={() => setTimeout(() => setShowResults(false), 200)}
          />
        </InputGroup>
        <Form.Text className="text-muted">Type a city, address, or landmark to search for a location</Form.Text>

        {/* Search Results Dropdown */}
        {showResults && searchResults.length > 0 && (
          <div className="position-absolute w-100 bg-white border rounded shadow-sm mt-1" style={{ zIndex: 1001 }}>
            {searchResults.map((result, index) => (
              <div
                key={index}
                className="p-2 border-bottom cursor-pointer hover-bg-light"
                style={{ cursor: "pointer" }}
                onMouseDown={() => selectSearchResult(result)}
                onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
                onMouseLeave={(e) => (e.target.style.backgroundColor = "white")}
              >
                <small className="text-muted">{result.display_name}</small>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Location Controls */}
      <div className="position-absolute top-0 end-0 m-2" style={{ zIndex: 1000 }}>
        <Button
          variant="success"
          size="sm"
          onClick={getCurrentLocation}
          disabled={gettingLocation || loading}
          className="shadow-sm"
        >
          {gettingLocation ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              Getting Location...
            </>
          ) : (
            <>
              <i className="fas fa-location-arrow me-1"></i>
              My Location
            </>
          )}
        </Button>
      </div>

      {/* Location Error Alert */}
      {locationError && (
        <Alert variant="warning" className="mb-2" dismissible onClose={() => setLocationError(null)}>
          <small>{locationError}</small>
        </Alert>
      )}

      <div style={{ height: "400px", width: "100%" }}>
        {position ? (
          <MapContainer center={position} zoom={13} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <Marker
              position={position}
              draggable={true}
              eventHandlers={{
                dragend: handleMarkerDragEnd,
              }}
            />
            <MapEvents />
          </MapContainer>
        ) : (
          <p>Loading map...</p>
        )}
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75 rounded">
          <div className="text-center">
            <Spinner animation="border" variant="success" />
            <div className="mt-2 fw-medium">Analyzing location...</div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MapComponent
